import CollapsingDisabledMedicalQuestionDecorator from "../Components/CollapsingDisabledMedicalQuestionDecorator";
import NoElementFormItem from "../Components/NoElementFormItem";
import SideEffectMedicalQuestionDecorator from "../Components/SideEffectMedicalQuestionDecorator";
import YesNoToggleMedicalQuestion from "../Components/YesNoToggleMedicalQuestion";
import ComposableMedicalFormEvaluator, {
  IComposableMedicalFormEvaluatorConfig
} from "../Types/ComposableMedicalFormEvaluator";
import IMedicalFormElementProps from "../Types/IMedicalFormElement";
import IMedicalQuestionnaireCompleteEvaluator from "../Types/IMedicalQualificationCompleteEvaluator";
import IMedicalQualificationNonCandidateEvaluator from "../Types/IMedicalQualificationNonCandidateEvaluator";

const questionIds = {
  pacemaker: "pacemakerMedicalQuestions.pacemaker",
  defibrillator: "pacemakerMedicalQuestions.defibrillator"
};

const noDefibrillatorDialogueFormItem: React.FC<IMedicalFormElementProps> = props =>
  NoElementFormItem({
    ...props,
    questionId: "pacemakerMedicalQuestions.noDefibrillatorDialogue",
    text: "Thank you for sharing. During your consultation, we will review your medical history in greater detail as this could impact your eligibility for surgery.",
    ChildFormItem: null
  });

const collapseNoDefibrillatorDialogueDecorator: React.FC<IMedicalFormElementProps> = props =>
  CollapsingDisabledMedicalQuestionDecorator({
    ...props,
    isDisabled: formState => formState[questionIds.defibrillator] !== false,
    ChildFormItem: noDefibrillatorDialogueFormItem
  });

const defibrillatorQuestion: React.FC<IMedicalFormElementProps> = props =>
  YesNoToggleMedicalQuestion({
    ...props,
    questionId: questionIds.defibrillator,
    text: "Does it have a defibrillator?",
    ChildFormItem: collapseNoDefibrillatorDialogueDecorator
  });

const collapseDefibrillatorDecorator: React.FC<IMedicalFormElementProps> = props =>
  CollapsingDisabledMedicalQuestionDecorator({
    ...props,
    isDisabled: formState => formState[questionIds.pacemaker] !== true,
    ChildFormItem: defibrillatorQuestion
  });

const conclusionDefibrillatorDecorator: React.FC<IMedicalFormElementProps> = props =>
  SideEffectMedicalQuestionDecorator({
    ...props,
    transformMedicalConclusions: (oldConclusions, newFormState) => ({
      ...oldConclusions,
      hasPacemakerWithoutDefibrillator: newFormState[questionIds.defibrillator] === false
    }),
    transformMedicalFormState: null,
    ChildFormItem: collapseDefibrillatorDecorator
  });

const pacemakerQuestion: React.FC<IMedicalFormElementProps> = props =>
  YesNoToggleMedicalQuestion({
    ...props,
    questionId: questionIds.pacemaker,
    text: "Do you have a pacemaker?",
    ChildFormItem: conclusionDefibrillatorDecorator
  });

const sideEffectsPacemakerQuestion: React.FC<IMedicalFormElementProps> = props =>
  SideEffectMedicalQuestionDecorator({
    ...props,
    transformMedicalConclusions: (previousConclusions, formState) =>
      formState[questionIds.pacemaker] === false
        ? { ...previousConclusions, hasPacemakerWithoutDefibrillator: null }
        : null,
    transformMedicalFormState: oldState =>
      oldState[questionIds.pacemaker] === false ? { ...oldState, [questionIds.defibrillator]: null } : null,
    ChildFormItem: pacemakerQuestion
  });

export const PacemakerFormEvaluatorConfig: IComposableMedicalFormEvaluatorConfig = {
  allFormQuestionIds: Object.values(questionIds),
  requiredQuestionPredicates: { [questionIds.defibrillator]: form => form[questionIds.pacemaker] === true },
  nonCandidateIgnoredQuestionIds: [questionIds.pacemaker],
  nonCandidateQuestionPredicates: null
};

export const PacemakerFormEvaluator: IMedicalQuestionnaireCompleteEvaluator &
  IMedicalQualificationNonCandidateEvaluator = new ComposableMedicalFormEvaluator(PacemakerFormEvaluatorConfig);

export const pacemakerFormQuestions: React.FC<IMedicalFormElementProps>[] = [sideEffectsPacemakerQuestion];
